import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { logger } from '@/lib/logger';
import { useViewHealthScope } from '@/lib/view-health';

interface DeleteEntityTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
  entityTypeName: string; // Internal name like 'org_foo'
  entityDisplayName: string; // User-visible label
  entityDisplayNamePlural: string; // Plural label
  orgId: number;
  isPipelineable: boolean;
}

export default function DeleteEntityTypeModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
  entityTypeName,
  entityDisplayName,
  entityDisplayNamePlural,
  orgId,
  isPipelineable,
}: DeleteEntityTypeModalProps) {
  useViewHealthScope('overlay', 'delete-entity-type-modal', isOpen);
  const [error, setError] = useState<string | null>(null);

  // Fetch object count
  const { data: objectsData, isLoading: isLoadingObjects } = useQuery({
    queryKey: ['deleteEntityTypeModal', 'objectCount', orgId, entityTypeName],
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, entityTypeName);
      const response = await client.list({
        page_size: 1,
        page: 1,
        properties: [],
      });
      return response;
    },
    enabled: isOpen,
  });

  // Fetch pipeline count (only if pipelineable)
  const { data: pipelinesData, isLoading: isLoadingPipelines } = useQuery({
    queryKey: ['deleteEntityTypeModal', 'pipelineCount', orgId, entityTypeName],
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, `${entityTypeName}_pipeline`);
      const response = await client.list({
        page_size: 1,
        page: 1,
        properties: [],
      });
      return response;
    },
    enabled: isOpen && isPipelineable,
  });

  const isLoading = isLoadingObjects || (isPipelineable && isLoadingPipelines);
  const objectCount = objectsData?.count ?? 0;
  const pipelineCount = pipelinesData?.count ?? 0;

  const handleConfirm = useCallback(async () => {
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      logger.error(
        'Delete object type failed',
        err instanceof Error ? err : undefined,
        { component: 'crm_list' }
      );
      setError("We couldn't delete this object type. Try again.");
      throw err; // Re-throw so modal knows it failed
    }
  }, [onConfirm]);

  const hasData = objectCount > 0 || (isPipelineable && pipelineCount > 0);

  let description: string;
  if (isLoading || !hasData) {
    description = `Are you sure you want to delete the "${entityDisplayName}" object type? This cannot be undone.`;
  } else {
    // Build a human-readable summary of what will be deleted
    const parts: string[] = [];
    if (objectCount > 0) {
      parts.push(
        `${objectCount} ${objectCount === 1 ? entityDisplayName.toLowerCase() : entityDisplayNamePlural.toLowerCase()}`
      );
    }
    if (isPipelineable && pipelineCount > 0) {
      parts.push(
        `${pipelineCount} ${pipelineCount === 1 ? 'pipeline' : 'pipelines'}`
      );
    }
    description = `This will permanently delete ${parts.join(' and ')}. This cannot be undone.`;
  }

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={handleConfirm}
      title={`Delete ${entityDisplayName}`}
      description={description}
      confirmLabel={isDeleting ? 'Deleting…' : 'Delete'}
      isLoading={isDeleting}
      variant='destructive'
    >
      {error && <p className='text-sm text-error-primary'>{error}</p>}
    </ConfirmationDialog>
  );
}
